import React, {useEffect, useState} from 'react';
import {CommonLayout} from "../../layouts/CommonLayout/CommonLayout";
import {Link, useParams} from "react-router-dom";
import DocumentsTreeView from "../../components/DocumentsTreeView/DocumentsTreeView.jsx";
import {Backdrop, Button, CircularProgress, IconButton, Menu, MenuItem, Paper} from "@material-ui/core";
import {makeStyles} from "@material-ui/styles";
import {useDispatch, useSelector} from "react-redux";
import {
    createNormativeArticle,
    createNormativeChapter,
    deleteElement,
    downloadNormativeDocument,
    getFolderNodes,
    getNormativeDocumentNodes,
    moveNormativeDocumentTreeElement,
    selectLoading,
    selectNormativeArticles,
    selectNormativeChapters,
    selectNormativeDocuments,
    selectNormativeDocumentTree,
    updateNormativeArticle,
    updateNormativeChapter,
    updateNormativeDocument
} from "../../redux/reducers/normative-documents-reducer.js";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ViewStreamIcon from '@material-ui/icons/ViewStream';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import {RouteNames} from "../../router/consts";
import EditDocumentDialog from "../../components/DocumentsTreeDialogs/EditDocumentDialog.jsx";
import {useDialog} from "../../hooks/useDialog.js";
import DeleteDialog from "../../components/DeleteDialog/DeleteDialog.jsx";
import AddNormativeChapterDialog from "../../components/DocumentsTreeDialogs/AddNormativeChapterDialog.jsx";
import AddNormativeArticleDialog from "../../components/DocumentsTreeDialogs/AddNormativeArticleDialog.jsx";
import EditNormativeChapterDialog from "../../components/DocumentsTreeDialogs/EditNormativeChapterDialog.jsx";
import EditNormativeArticleDialog from "../../components/DocumentsTreeDialogs/EditNormativeArticleDialog.jsx";
import {createInfoNotification} from "../../redux/reducers/notification-reducer.js";

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(1),
        width: '100%',
    },
    header: {
        paddingBottom: theme.spacing(1)
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
}))

const AddMenu = (props) => {
    const {anchorEl, handleClose, title, onAddNormativeChapterButtonClick, onAddNormativeArticleButtonClick} = props

    const handleAddNormativeChapterButtonClick = () => {
        handleClose()
        onAddNormativeChapterButtonClick()
    }

    const handleAddNormativeArticleButtonClick = () => {
        handleClose()
        onAddNormativeArticleButtonClick()
    }

    return (
        <Menu
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
        >
            <MenuItem onClick={handleAddNormativeChapterButtonClick}>Добавить главу в {title}</MenuItem>
            <MenuItem onClick={handleAddNormativeArticleButtonClick} disabled={title === 'корень документа'}>Добавить
                пункт в {title}</MenuItem>
        </Menu>
    );
}

const NormativeDocumentEditPage = () => {
    const {id} = useParams()

    const classes = useStyles()

    const [selectedNode, setSelectedNode] = useState(null)
    const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null)
    const [parentNormativeChapterID, setParentNormativeChapterID] = useState(null)
    const [editNormativeChapterID, setEditNormativeChapterID] = useState(null)
    const [editNormativeArticleID, setEditNormativeArticleID] = useState(null)
    const [cutNode, setCutNode] = useState('')

    const documentNodes = useSelector(selectNormativeDocumentTree)
    const normativeDocuments = useSelector(selectNormativeDocuments)
    const normativeChapters = useSelector(selectNormativeChapters)
    const normativeArticles = useSelector(selectNormativeArticles)
    const loading = useSelector(selectLoading)

    const editDocumentDialog = useDialog()
    const addNormativeChapterDialog = useDialog()
    const editNormativeChapterDialog = useDialog()
    const addNormativeArticleDialog = useDialog()
    const editNormativeArticleDialog = useDialog()
    const deleteDialog = useDialog()

    const dispatch = useDispatch()

    const getParentNode = (selectedNode) => {
        const [type, id] = selectedNode.split('.')
        if (id === undefined) return null
        if (type === 'normativeArticle') {
            return normativeArticles.find(article => article.id === parseInt(id)).normative_chapter
        }
        if (type === 'normativeChapter') {
            return normativeChapters.find(chapter => chapter.id === parseInt(id)).id
        }
    }

    const handleCloseAddMenu = () => {
        setAddMenuAnchorEl(null)
    }

    const typeIcons = {
        normativeChapter: ViewStreamIcon,
        normativeArticle: FiberManualRecordIcon
    }

    const iconTooltips = {
        normativeChapter: 'Глава',
        normativeArticle: 'Пункт'
    }

    const toolbarCallbacks = {
        onAdd: (nodeID) => async (event) => {
            await setParentNormativeChapterID(getParentNode(nodeID))
            await setAddMenuAnchorEl(event.target)
        },
        onEdit: (nodeID) => async () => {
            const [type, id] = nodeID.split('.')
            if (type === 'normativeChapter') {
                await setEditNormativeChapterID(id)
                editNormativeChapterDialog.handleOpen()
            }
            if (type === 'normativeArticle') {
                await setEditNormativeArticleID(id)
                editNormativeArticleDialog.handleOpen()
            }
        },
        onDelete: (nodeID) => async () => {
            await setSelectedNode(nodeID)
            await deleteDialog.handleOpen()
        },
        onCut: (nodeID) => async () => {
            await setCutNode(nodeID)
            dispatch(createInfoNotification('Вырезано'))
        },
        onPaste: (nodeID) => async () => {
            const result = await dispatch(moveNormativeDocumentTreeElement(cutNode, getParentNode(nodeID)))
            if (result === null) return null
            await setCutNode('')
            await dispatch(getNormativeDocumentNodes(id))
        },
        onDoubleClick: (nodeID) => async () => {
            const [type, id] = nodeID.split('.')
            if (type === 'normativeChapter') {
                await setEditNormativeChapterID(id)
                editNormativeChapterDialog.handleOpen()
            }
            if (type === 'normativeArticle') {
                await setEditNormativeArticleID(id)
                editNormativeArticleDialog.handleOpen()
            }
        }
    }

    useEffect(() => {
        if (normativeDocuments.length === 0) {
            dispatch(getFolderNodes())
        }
    }, [normativeDocuments])

    useEffect(() => {
        dispatch(getNormativeDocumentNodes(id))
    }, [id])

    return (
        <CommonLayout>
            <Backdrop className={classes.backdrop} open={Boolean(loading)}>
                <CircularProgress color={'inherit'}/>
            </Backdrop>

            <EditDocumentDialog
                open={editDocumentDialog.open}
                onClose={editDocumentDialog.handleClose}
                initialValues={normativeDocuments.find(doc => doc.id === parseInt(id))}
                onDownloadFile={async (id) => {
                    await dispatch(downloadNormativeDocument(id))
                }}
                submitCallback={async (values) => {
                    await dispatch(updateNormativeDocument(values))
                    await dispatch(getFolderNodes())
                }}
            />
            <AddMenu
                anchorEl={addMenuAnchorEl}
                handleClose={handleCloseAddMenu}
                title={parentNormativeChapterID === null
                    ? 'корень документа'
                    : normativeChapters.find(chapter => chapter.id === parentNormativeChapterID)?.name}
                onAddNormativeChapterButtonClick={addNormativeChapterDialog.handleOpen}
                onAddNormativeArticleButtonClick={addNormativeArticleDialog.handleOpen}
            />
            <AddNormativeChapterDialog
                open={addNormativeChapterDialog.open}
                onClose={addNormativeChapterDialog.handleClose}
                submitCallback={async (values) => {
                    await dispatch(createNormativeChapter({
                        ...values,
                        normative_document: id,
                        normative_chapter: parentNormativeChapterID
                    }))
                    await dispatch(getNormativeDocumentNodes(id))
                }}
            />
            <AddNormativeArticleDialog
                open={addNormativeArticleDialog.open}
                onClose={addNormativeArticleDialog.handleClose}
                submitCallback={async (values) => {
                    await dispatch(createNormativeArticle({
                        ...values,
                        normative_document: id,
                        normative_chapter: parentNormativeChapterID
                    }))
                    await dispatch(getNormativeDocumentNodes(id))
                }}
            />
            <EditNormativeChapterDialog
                open={editNormativeChapterDialog.open}
                onClose={editNormativeChapterDialog.handleClose}
                initialValues={normativeChapters.find(chapter => chapter.id === parseInt(editNormativeChapterID))}
                submitCallback={async (values) => {
                    await dispatch(updateNormativeChapter(values))
                    await dispatch(getNormativeDocumentNodes(id))
                }}
            />
            <EditNormativeArticleDialog
                open={editNormativeArticleDialog.open}
                onClose={editNormativeArticleDialog.handleClose}
                initialValues={normativeArticles.find(article => article.id === parseInt(editNormativeArticleID))}
                submitCallback={async (values) => {
                    await dispatch(updateNormativeArticle(values))
                    await dispatch(getNormativeDocumentNodes(id))
                }}
            />
            <DeleteDialog
                open={deleteDialog.open}
                onClose={deleteDialog.handleClose}
                loading={loading}
                submitCallback={async () => {
                    await dispatch(deleteElement(selectedNode))
                    await dispatch(getNormativeDocumentNodes(id))
                }}
            />

            <Grid container spacing={2} direction={'row'}>
                <Grid item>
                    <IconButton size={'small'} component={Link}
                                to={RouteNames.NORMATIVE_DOCUMENTS}><ArrowBackIcon/></IconButton>
                </Grid>
                <Grid item xs>
                    <Typography className={classes.header}
                                variant={'h5'}>{normativeDocuments.find(doc => doc.id === parseInt(id))?.name}</Typography>
                </Grid>
                <Grid item>
                    <Button
                        size={'small'}
                        variant={'outlined'}
                        onClick={editDocumentDialog.handleOpen}
                    >
                        Изменить
                    </Button>
                </Grid>
            </Grid>
            <Paper className={classes.paper}>
                <DocumentsTreeView
                    nodes={loading ? [] : documentNodes}
                    typeIcons={typeIcons}
                    iconTooltips={iconTooltips}
                    toolbarCallbacks={toolbarCallbacks}
                    isAbleToPaste={Boolean(cutNode)}
                />
            </Paper>
        </CommonLayout>
    );
};

export default NormativeDocumentEditPage;